import assignments from '~/data/rarity-assignments.json'
import type { CreatureDef, Rarity } from '~/types/game'

/**
 * Provisional, simulation-based rarity. Regenerate with `npm run simulate`
 * (writes rarity-assignments.json). Mostly visual, with one gameplay use
 * since v0.25: boss/elite reward floors (utils/rewards rarityFloor) filter
 * pools by tier — so re-running the simulation can shift what milestone
 * nodes offer. Regular drop weights remain untouched.
 */
export const RARITY_STATUS = assignments.status

/**
 * Static pins for items the simulation can't see or value (v0.76):
 * injury-gated relics never enter the ascension-0 sim, so their tier is
 * human judgment. Close Enough is LEGENDARY per Javi — on ascension runs
 * it defuses the whole injury system at high-chance dispatches.
 */
const STATIC_PINS: Record<string, Rarity> = {
  'relic-close-enough': 'Legendary',
  'relic-medic-band': 'Rare',
  // v0.77 (per Javi): cure tiers colored by potency; Team Journal's new
  // cure rider makes it purple.
  'miracle-salve': 'Epic',
  'trail-medkit': 'Rare',
  'field-dressing': 'Uncommon',
  'relic-team-journal': 'Epic',
  // v0.84/v0.85 (per Javi): sims can't value trained skills (bots never
  // learn them) — a free instant skill is a chase pick, LEGENDARY.
  'training-ground': 'Legendary',
  // v0.85 (per Javi): the economy relics under-read in sims exactly like
  // timer relics (bots can't price gold) — human judgment says Epic.
  'relic-midas-charm': 'Epic',
  'relic-haggler-charm': 'Epic',
  // v1.76 buffs (per Javi): Perfect Plan 1→5 XP and Specialist Badge
  // 10→15% are real picks now — pinned Uncommon until the next sim run.
  'relic-perfect-plan': 'Uncommon',
  'relic-specialist-badge': 'Uncommon',
}

export function rarityOf(id: string): Rarity {
  return STATIC_PINS[id]
    ?? (assignments.assignments as Record<string, Rarity>)[id]
    ?? 'Common'
}

export const RARITY_COLORS: Record<Rarity, string> = {
  Common: '#9aa5b1',
  Uncommon: '#43a047',
  Rare: '#2f80ed',
  Epic: '#9b59d0',
  Legendary: '#f2a900',
}

/** 🛡️ The ink for a tier WORD drawn with no badge beside it - the in-run
 * relic list, the relic inspector, the Reference fold - where the word is
 * the only sign of the tier. v7.39 (per Javi, for his colour-blind
 * brother) moved these off their glow colours (legendary gold was ~1.7:1
 * on parchment) to dark tier inks; 🛡️ v7.41 (per Javi: "make it grey and
 * smaller") one warm grey, >= 4.5:1 on parchment and white. v7.41 later
 * took the name off every BADGE again (Javi: "remove the text for the
 * rarity"), so badges carry no word; these three keep theirs, in this
 * grey. Never a glow colour (RARITY_COLORS) as text. */
export const RARITY_WORD_INK = '#625a51'

/** 🛡️ Colorblind-safe tier badges (v1.61, per Javi): shown top-left of
 * every card that signals rarity, alongside the colored glow. Creature
 * rarity ('uncommon'/'rare') maps onto the same art. Since v7.39 every
 * surface draws them through components/RarityBadge.vue (🛡️ v7.41: the
 * badge alone again, its tier named in its alt text only).
 * 🏅 v7.50 (made by ChatGPT, per Javi: Common starts at one - art/UI/rarity-arrowheads-v5): sculpted
 * gold arrowheads with Common 1, Uncommon 2, Rare 3, Epic 4 and Legendary 5 main blades, so rank reads
 * without color; the gold faces carry the tier's reflected shine (silver-white, green, blue, purple,
 * amber). Replaces v7.49's 3-7 stars. The asset revision refreshes cached art. */
export const RARITY_BADGES: Partial<Record<Rarity, string>> = {
  Common: '/ui/badge-common.webp?v=arrowheads-5',
  Uncommon: '/ui/badge-uncommon.webp?v=arrowheads-5',
  Rare: '/ui/badge-rare.webp?v=arrowheads-5',
  Epic: '/ui/badge-epic.webp?v=arrowheads-5',
  Legendary: '/ui/badge-legendary.webp?v=arrowheads-5',
}

/* 🛡️ v7.41 (per Javi): creatureRarityBadge (v2.75, creature rarity -> badge
 * art) is gone - every badge is <RarityBadge :tier="creatureRarityKey(...)">
 * now, so epics and legendaries still get their real badge through the key. */

/** The glow colour that has to agree with the badge above. */
export function creatureRarityColor(rarity: CreatureDef['rarity']): string {
  return RARITY_COLORS[creatureRarityKey(rarity)]
}

/** 'rare' → 'Rare'. Exported since v3.49 so the Collection can sort and
 * filter creatures on the same ladder as relics and rewards. */
export function creatureRarityKey(rarity: CreatureDef['rarity']): Rarity {
  const key = rarity ?? 'uncommon'
  return (key[0]!.toUpperCase() + key.slice(1)) as Rarity
}
